/**
 * Admin Startseite · Section-Editor (Phase 2b).
 * Liest alle `home.*`-Section-Keys aus site_content, erlaubt inline-Edit
 * strukturierter JSON-Objekte, speichert per PUT je geänderter Section.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
    Save, Check, ExternalLink, AlertCircle,
    Layers, Sparkles, Lightbulb, Globe, Focus, GitCompare, LayoutGrid,
    Image as ImageIcon, SplitSquareHorizontal, RotateCw, ListOrdered,
    Package, Users, HelpCircle, Mail,
} from "lucide-react";
import { api } from "@/lib/auth";
import type { MediaRef } from "@/lib/media";
import { invalidateSiteContentCache } from "@/lib/siteContent";
import { USE_CASES as USE_CASES_FALLBACK } from "@/lib/homeContent";
import {
    SectionCard, TextField, TextareaField, ToggleField, SelectField,
    MediaField, MediaListField, RepeaterField,
} from "@/components/admin/editor/fields";
import MediaPicker, { type PickerContext } from "@/components/admin/MediaPicker";
import TabNavigation, { type Tab } from "@/components/admin/TabNavigation";
import SectionEnabledToggle from "@/components/admin/SectionEnabledToggle";
import BrandLogoPanel from "@/pages/admin/BrandLogoPanel";
import ChromaTunePanel from "@/pages/admin/ChromaTunePanel";
import RichTextField from "@/components/admin/RichTextField";

/** Spezielle Tab-ID für die „Alle Sektionen"-Ansicht (alles untereinander). */
const ALL_TAB_ID = "all";

/** Icon-Mapping pro Sektion (Tab-ID = Key-Suffix). Lucide 14 px Standard. */
const TAB_ICONS: Record<string, ReactElement> = {
    hero: <Sparkles size={14} />,
    idea: <Lightbulb size={14} />,
    immersive: <Globe size={14} />,
    lenses: <Focus size={14} />,
    compare: <GitCompare size={14} />,
    use_cases: <LayoutGrid size={14} />,
    gallery: <ImageIcon size={14} />,
    before_after: <SplitSquareHorizontal size={14} />,
    transformations360: <RotateCw size={14} />,
    process: <ListOrdered size={14} />,
    packages: <Package size={14} />,
    about: <Users size={14} />,
    faq: <HelpCircle size={14} />,
    contact: <Mail size={14} />,
};

const HOME_KEYS = [
    "home.hero", "home.idea", "home.immersive", "home.lenses",
    "home.compare", "home.use_cases", "home.gallery", "home.before_after",
    "home.transformations360",
    "home.process", "home.packages", "home.about", "home.faq", "home.contact",
] as const;

type HomeSections = Record<string, Record<string, unknown>>;

/* Section-Meta für Header/Anchor */
const META: Record<string, { title: string; anchor: string; description: string }> = {
    "home.hero": { title: "Hero", anchor: "hero", description: "Erscheint ganz oben auf der Startseite. Hintergrund kann Foto, 360°-Foto, Video oder 360°-Video sein — der Renderer wählt automatisch die richtige Anzeige. Wenn kein Medium gesetzt ist, greift die gewählte Vorlage-Variante." },
    "home.idea": { title: "Die Idee", anchor: "idea", description: "Zweispaltige Text/Medium-Section direkt nach dem Hero. Alle Medientypen unterstützt (Bild, 360°-Bild, Video, 360°-Video)." },
    "home.immersive": { title: "360°-Erlebnis", anchor: "immersive", description: "Zeige bis zu vier 360°-Videos als Kachel-Galerie. Jede Kachel öffnet den interaktiven 360°-Player. Aktiviere/deaktiviere einzelne Videos ohne sie zu löschen." },
    "home.lenses": { title: "Zwei Linsen", anchor: "lenses", description: "Technik-Erklärung — kein Medium, SVG bleibt codebasiert." },
    "home.compare": { title: "Vergleich", anchor: "compare", description: "Foto vs. Video vs. 360° — drei erklärende Karten." },
    "home.use_cases": { title: "Einsatz", anchor: "use-cases", description: "Kachel-Raster (Sektion 'Einsatz' auf der Startseite): Wann HypeFly der richtige Zeitpunkt ist. Jede Kachel unterstützt alle Medientypen (Foto, 360°-Foto, Video, 360°-Video). Über den Skybox-Slot kann pro Kachel ein zusätzliches 360°-Erlebnis geöffnet werden." },
    "home.gallery": { title: "Galerie", anchor: "gallery", description: "Mediengalerie mit Foto/360°-Foto-Kacheln. Öffnet im Lightbox-Modus." },
    "home.before_after": { title: "Vorher / Nachher", anchor: "compare", description: "Bild-Slider zum direkten Vergleich. Beide Bilder sollten dieselbe Perspektive und Auflösung haben. Nur Fotos (kein Video)." },
    "home.transformations360": { title: "360° Transformationen", anchor: "transformations360", description: "Signature-Feature: 360°-Vorher/Nachher-Rundgänge. Kacheln auf der Startseite öffnen einen Fullscreen-Modal mit interaktivem Split-Slider zwischen den beiden Panoramen. Vorher & Nachher MÜSSEN 360°-Fotos sein, Thumbnail darf ein normales Foto sein." },
    "home.about": { title: "Über uns", anchor: "about", description: "Editorial-Text mit begleitendem Medium. Alle Medientypen unterstützt." },
    "home.faq": { title: "FAQ", anchor: "faq", description: "Häufig gestellte Fragen — nur Text, kein Medium." },
    "home.cta": { title: "Kontakt-CTA", anchor: "cta", description: "Abschluss-Aufruf — nur Text." },
    "home.process": { title: "Ablauf", anchor: "process", description: "Vier Schritte vom Erstgespräch bis zur Übergabe." },
    "home.packages": { title: "Pakete", anchor: "packages", description: "Drei Pakete: Essential, Signature, Legacy." },
    "home.contact": { title: "Kontakt", anchor: "kontakt", description: "Telefon, E-Mail, Anschrift der BDV." },
};

function deepEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export default function AdminStartseitePage() {
    const [saved, setSaved] = useState<HomeSections>({});
    const [edit, setEdit] = useState<HomeSections>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState<"ok" | "err" | null>(null);
    const [flashMsg, setFlashMsg] = useState("");
    const [pickerCtx, setPickerCtx] = useState<PickerContext>(null);
    // media_id des aktuell in `brand_logo` gespeicherten Assets — vom
    // BrandLogoPanel gemeldet. Wird an das ChromaTunePanel weitergereicht.
    const [brandLogoMediaId, setBrandLogoMediaId] = useState<string | null>(null);

    /* -------- Tabs: „Alle" als erster + eine Sektion pro Tab.
     *  Tab-ID = Key-Suffix (unique), denn `META[k].anchor` ist NICHT unique
     *  (home.compare + home.before_after teilen sich denselben Anchor
     *  "compare"). -------- */
    const tabIdFor = (k: string): string => k.split(".").pop() ?? k;
    const tabs = useMemo<Tab[]>(() => {
        const sectionTabs: Tab[] = HOME_KEYS.map((k) => {
            const id = tabIdFor(k);
            return { id, label: META[k].title, icon: TAB_ICONS[id] };
        });
        return [
            { id: ALL_TAB_ID, label: "Alle", icon: <Layers size={15} /> },
            ...sectionTabs,
        ];
    }, []);
    const firstTabId = tabs[0]?.id ?? ALL_TAB_ID;
    // Alte Bookmark-/Label-Hashes zu tab-IDs mappen, damit User nicht auf
    // firstTab zurückfallen wenn sie z.B. `#einsatz` oder `#möglichkeiten`
    // aus alter Zeit in der URL haben (Tab-ID ist `use_cases`).
    const HASH_ALIASES: Record<string, string> = {
        einsatz: "use_cases",
        möglichkeiten: "use_cases",
        moeglichkeiten: "use_cases",
    };
    const readHash = (): string | null => {
        if (typeof window === "undefined") return null;
        const raw = window.location.hash.replace(/^#/, "").toLowerCase();
        if (!raw) return null;
        const mapped = HASH_ALIASES[raw] ?? raw;
        return tabs.some((t) => t.id === mapped) ? mapped : null;
    };
    const [activeTab, setActiveTab] = useState<string>(() => readHash() ?? firstTabId);

    // URL-Hash-Sync: Tab-Wechsel schreibt Hash (history.replaceState → kein
    // Neu-Rendering, kein Router-Trigger). Beim Reload landet der User auf
    // demselben Tab, Links sind teilbar.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const desired = `#${activeTab}`;
        if (window.location.hash !== desired) {
            window.history.replaceState(null, "", desired);
        }
    }, [activeTab]);
    useEffect(() => {
        const onHash = () => {
            if (typeof window === "undefined") return;
            const h = window.location.hash.replace(/^#/, "");
            if (h && tabs.some((t) => t.id === h) && h !== activeTab) {
                setActiveTab(h);
            }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, [activeTab, tabs]);

    /* -------- Load -------- */
    useEffect(() => {
        void (async () => {
            const res = await api<{ items: Record<string, unknown> }>(
                "/api/site-content?prefix=home.",
            );
            const items = res.ok && res.data ? res.data.items : {};
            const sections: HomeSections = {};
            for (const k of HOME_KEYS) {
                const v = items[k];
                sections[k] = (v && typeof v === "object" && !Array.isArray(v))
                    ? (v as Record<string, unknown>)
                    : {};
            }
            setSaved(sections);
            setEdit(JSON.parse(JSON.stringify(sections)));
            setLoading(false);
        })();
    }, []);

    /* -------- Change detection -------- */
    const changedKeys = HOME_KEYS.filter((k) => !deepEqual(saved[k], edit[k]));
    const hasChanges = changedKeys.length > 0;

    /* -------- Beforeunload warning -------- */
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!hasChanges) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasChanges]);

    const setSection = useCallback((key: string, value: Record<string, unknown>) => {
        setEdit((prev) => ({ ...prev, [key]: value }));
    }, []);

    /* -------- Save -------- */
    const doSave = async () => {
        if (!hasChanges) return;
        setSaving(true);
        setFlash(null);
        const errors: string[] = [];
        const nextSaved: HomeSections = { ...saved };
        for (const k of changedKeys) {
            const res = await api(`/api/admin/site-content/${encodeURIComponent(k)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: edit[k] }),
            });
            if (res.ok) {
                nextSaved[k] = JSON.parse(JSON.stringify(edit[k]));
            } else {
                errors.push(`${k}: ${res.error || "unknown"}`);
            }
        }
        setSaved(nextSaved);
        setSaving(false);
        // Cache invalidieren, damit nächster Home-Reload sofort frische Werte sieht
        invalidateSiteContentCache();
        if (errors.length) {
            setFlash("err");
            setFlashMsg(`Fehler bei ${errors.length}/${changedKeys.length}: ${errors.join(" · ")}`);
        } else {
            setFlash("ok");
            setFlashMsg(`Gespeichert (${changedKeys.length} ${changedKeys.length === 1 ? "Section" : "Sections"})`);
            setTimeout(() => setFlash(null), 2000);
        }
    };

    const doDiscard = () => {
        if (!hasChanges) return;
        if (!confirm("Alle ungespeicherten Änderungen verwerfen?")) return;
        setEdit(JSON.parse(JSON.stringify(saved)));
    };

    /* Helper: open picker */
    const openPicker = useCallback((currentType?: string | string[], onPick?: (id: string, p: string) => void) => {
        if (!onPick) return;
        setPickerCtx({ type: currentType, onPick });
    }, []);

    if (loading) return <div className="text-sm text-hf-muted">Lade…</div>;

    /* -------- Dirty-Marker pro Tab (id = Key-Suffix). -------- */
    const dirtyTabIds = new Set<string>();
    for (const k of HOME_KEYS) {
        if (!deepEqual(saved[k], edit[k])) dirtyTabIds.add(tabIdFor(k));
    }

    /* -------- Aktiven Section-Key aus activeTab ableiten. -------- */
    const activeKey = HOME_KEYS.find((k) => tabIdFor(k) === activeTab) ?? HOME_KEYS[0];
    const activeMeta = META[activeKey];

    // media_id des aktuell in `brand_logo` gespeicherten Assets — vom
    // BrandLogoPanel gemeldet. Wird an das ChromaTunePanel darunter durch-
    // gereicht, damit dessen Live-Preview & Save-Ziel identisch mit dem
    // ausgewählten Logo-Video sind.

    return (
        <div data-testid="admin-startseite">
            {/* Branding · Header-Logo (Bild oder Video). Steht bewusst
             *  oberhalb der Sticky-Editor-Bar, damit es beim Scrollen
             *  verschwindet und den Section-Editor nicht überlagert. */}
            <BrandLogoPanel setPickerCtx={setPickerCtx} onSavedIdChange={setBrandLogoMediaId} />
            {/* Chroma-Key-Feintuning: nur für Video-Media-Docs sichtbar. */}
            <ChromaTunePanel mediaId={brandLogoMediaId} />

            {/* Sticky Header: Save-Bar + Tab-Leiste in einem Sticky-Container. */}
            <div
                className="sticky top-0 z-40 -mx-6 md:-mx-8 mb-8 backdrop-blur"
                style={{ background: "hsl(var(--hf-bg) / 0.94)" }}
            >
                <div
                    className="px-6 md:px-8 py-4 flex items-center justify-between"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}
                >
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Admin › Startseite</div>
                        <h1 className="font-display text-2xl md:text-3xl leading-tight mt-0.5">Startseite bearbeiten</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs md:text-sm px-3 py-2 rounded-sm border"
                            style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                            data-testid="editor-preview-link"
                        >
                            <ExternalLink size={14} /> Live-Vorschau
                        </a>
                        <button
                            type="button"
                            onClick={doDiscard}
                            disabled={!hasChanges || saving}
                            data-testid="editor-discard-button"
                            className="text-xs md:text-sm px-3 py-2 rounded-sm border disabled:opacity-30"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                        >
                            Verwerfen
                        </button>
                        <button
                            type="button"
                            onClick={doSave}
                            disabled={!hasChanges || saving}
                            data-testid="editor-save-button"
                            className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-sm font-medium disabled:opacity-40"
                            style={{
                                background: hasChanges ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))",
                                color: "hsl(var(--hf-ink))",
                            }}
                        >
                            {flash === "ok" ? <Check size={14} /> : <Save size={14} />}
                            <span>
                                {saving ? "Speichere…" : hasChanges ? `Speichern · ${changedKeys.length}` : "Gespeichert"}
                            </span>
                        </button>
                    </div>
                </div>
                <TabNavigation
                    tabs={tabs}
                    activeId={activeTab}
                    onChange={setActiveTab}
                    dirtyIds={dirtyTabIds}
                    testId="admin-startseite-tabs"
                    ariaLabel="Startseiten-Sektionen"
                    allTabId={ALL_TAB_ID}
                />
            </div>

            {flash === "err" && (
                <div
                    className="mb-6 p-4 rounded-sm text-sm flex items-start gap-2"
                    style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                >
                    <AlertCircle size={16} className="mt-0.5" /> {flashMsg}
                </div>
            )}

            {/* Tab-Panel: „Alle" → alle Sektionen untereinander (Scroll wie
             *  gehabt), oberhalb ein kompaktes 2-Spalten-Sichtbarkeits-Panel.
             *  Sonst → nur die aktive Sektion. Der Editor-State (`edit`) hält
             *  zentral alle Sections; das Wechseln der Ansicht ändert nur das
             *  Rendering, kein Dirty-State geht verloren. Alle Sichtbarkeits-
             *  Toggles (Overview + Panel-Header) mutieren dasselbe `edit`-
             *  Objekt und bleiben so bidirektional synchron. */}
            <div
                role="tabpanel"
                id={`tabpanel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
                className="space-y-6"
            >
                {activeTab === ALL_TAB_ID ? (
                    <>
                        <section
                            className="p-5 md:p-6 rounded-md"
                            style={{
                                background: "hsl(var(--hf-surface))",
                                border: "1px solid hsl(var(--hf-line))",
                                boxShadow: "var(--shadow-warm-sm)",
                            }}
                            data-testid="all-sections-overview"
                        >
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Übersicht</div>
                                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">Alle Sektionen · Sichtbarkeit</h2>
                                </div>
                                <span
                                    className="text-[11px]"
                                    style={{ color: "hsl(var(--hf-muted))" }}
                                    data-testid="all-sections-overview-hint"
                                >
                                    {HOME_KEYS.filter((k) => (edit[k] as { enabled?: boolean })?.enabled === false).length} von {HOME_KEYS.length} ausgeblendet
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {HOME_KEYS.map((k) => (
                                    <SectionEnabledToggle
                                        key={k}
                                        enabled={(edit[k] as { enabled?: boolean })?.enabled}
                                        onChange={(next) => setSection(k, { ...edit[k], enabled: next })}
                                        label={META[k].title}
                                        testId={`overview-toggle-${tabIdFor(k)}`}
                                    />
                                ))}
                            </div>
                        </section>
                        {HOME_KEYS.map((k) => (
                            <SectionCard
                                key={k}
                                sectionKey={k}
                                title={META[k].title}
                                description={META[k].description}
                                anchor={META[k].anchor}
                                dirty={!deepEqual(saved[k], edit[k])}
                            >
                                <SectionEnabledToggle
                                    enabled={(edit[k] as { enabled?: boolean })?.enabled}
                                    onChange={(next) => setSection(k, { ...edit[k], enabled: next })}
                                    testId={`panel-toggle-${tabIdFor(k)}`}
                                    hint={k === "home.hero"
                                        ? "Ohne Hero verliert die Startseite den primären Blickfang – nur ausschalten wenn absichtlich Landing-Variante ohne Hero gewünscht."
                                        : undefined}
                                />
                                <SectionEditor
                                    sectionKey={k}
                                    value={edit[k]}
                                    onChange={(v) => setSection(k, v)}
                                    openPicker={openPicker}
                                />
                            </SectionCard>
                        ))}
                    </>
                ) : (
                    <SectionCard
                        key={activeKey}
                        sectionKey={activeKey}
                        title={activeMeta.title}
                        description={activeMeta.description}
                        anchor={activeMeta.anchor}
                        dirty={!deepEqual(saved[activeKey], edit[activeKey])}
                    >
                        <SectionEnabledToggle
                            enabled={(edit[activeKey] as { enabled?: boolean })?.enabled}
                            onChange={(next) => setSection(activeKey, { ...edit[activeKey], enabled: next })}
                            testId={`panel-toggle-${tabIdFor(activeKey)}`}
                            hint={activeKey === "home.hero"
                                ? "Ohne Hero verliert die Startseite den primären Blickfang – nur ausschalten wenn absichtlich Landing-Variante ohne Hero gewünscht."
                                : undefined}
                        />
                        <SectionEditor
                            sectionKey={activeKey}
                            value={edit[activeKey]}
                            onChange={(v) => setSection(activeKey, v)}
                            openPicker={openPicker}
                        />
                    </SectionCard>
                )}
            </div>

            <MediaPicker ctx={pickerCtx} onClose={() => setPickerCtx(null)} />
        </div>
    );
}

/* -------- Section-Editor Router -------- */
function SectionEditor({
    sectionKey, value, onChange, openPicker,
}: {
    sectionKey: string;
    value: Record<string, unknown>;
    onChange: (v: Record<string, unknown>) => void;
    openPicker: (t?: string | string[], cb?: (id: string, p: string) => void) => void;
}) {
    const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
    const s = value as Record<string, unknown>;
    const getStr = (k: string) => (typeof s[k] === "string" ? (s[k] as string) : "");
    const getArr = <T,>(k: string): T[] => (Array.isArray(s[k]) ? (s[k] as T[]) : []);
    const getMedia = (k: string) => (s[k] as MediaRef);

    switch (sectionKey) {
        case "home.hero": {
            type HeroSlide = { id?: string; media?: MediaRef; label?: string; enabled?: boolean };
            const slides = getArr<HeroSlide>("items");
            const legacyBg = getMedia("background");
            const hasLegacy = !!(legacyBg && (
                (typeof legacyBg === "object" && (legacyBg.media_id || legacyBg.path))
                || (typeof legacyBg === "string" && legacyBg.length)
            ));
            const adoptLegacy = () => {
                const nextItems: HeroSlide[] = [
                    { id: `h${Date.now()}`, media: legacyBg, enabled: true },
                    ...slides,
                ];
                // Setze items + entferne background in einem einzigen State-Push
                onChange({ ...value, items: nextItems, background: null });
            };
            return (
                <>
                    <SelectField
                        label="Hintergrund-Variante (Fallback wenn keine Slides)"
                        value={getStr("background_variant") || "bergisches-landhaus"}
                        onChange={(v) => set("background_variant", v)}
                        options={[
                            { value: "bergisches-landhaus", label: "Bergisches Landhaus (editorial)" },
                            { value: "klinker-familienhaus", label: "Klinker-Familienhaus (editorial)" },
                            { value: "landhaus-modern", label: "Modernes Landhaus (editorial)" },
                            { value: "villa-original", label: "Legacy Villa (hell/original)" },
                        ]}
                        testId="hero-variant"
                    />
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} testId="hero-eyebrow" />
                    <TextareaField label="Hauptüberschrift" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="hero-headline" rows={2} helpText="\n = Zeilenumbruch" />
                    <RichTextField label="Untertext" value={getStr("subline")} onChange={(v) => set("subline", v)} testId="hero-subline" rows={3} helpText="Editorial-Fließtext · Formatierung siehe Toolbar (z.B. Gold-Marker für Preise)." />
                    <RepeaterField<string>
                        label="USPs"
                        items={getArr<string>("usps")}
                        onChange={(v) => set("usps", v)}
                        newItem={() => ""}
                        maxItems={5}
                        testId="hero-usps"
                        renderItem={(it, i, upd) => (
                            <TextField label={`USP #${i + 1}`} value={it} onChange={upd} testId={`hero-usp-${i}`} />
                        )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="CTA primär Label" value={getStr("cta_primary_label")} onChange={(v) => set("cta_primary_label", v)} testId="hero-cta1" />
                        <TextField label="CTA sekundär Label" value={getStr("cta_secondary_label")} onChange={(v) => set("cta_secondary_label", v)} testId="hero-cta2" />
                    </div>
                    {hasLegacy && slides.length === 0 && (
                        <div
                            className="p-3 rounded-sm text-[12px] flex items-center justify-between gap-3"
                            style={{
                                background: "hsl(var(--hf-gold-soft))",
                                border: "1px solid hsl(var(--hf-gold-dark) / 0.4)",
                                color: "hsl(var(--hf-gold-dark))",
                            }}
                            data-testid="hero-legacy-migrate-banner"
                        >
                            <span>
                                Altes Einzel-Medium erkannt. Übernehmen, um es als ersten Slide im
                                Multi-Item-Hero zu nutzen — das alte Feld wird geleert.
                            </span>
                            <button
                                type="button"
                                onClick={adoptLegacy}
                                className="text-[11px] px-3 py-1.5 rounded-sm uppercase tracking-[0.14em] font-medium"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                    border: "1px solid hsl(var(--hf-gold-dark))",
                                }}
                                data-testid="hero-legacy-migrate-btn"
                            >
                                Als Slide übernehmen
                            </button>
                        </div>
                    )}
                    <RepeaterField<HeroSlide>
                        label="Hero-Slides (Multi-Item)"
                        items={slides}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ id: `h${Date.now()}`, media: null, label: "", enabled: true })}
                        testId="hero-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <MediaField
                                    label={`Slide #${i + 1} · Medium`}
                                    mediaRef={it.media as MediaRef}
                                    onChange={(v) => upd({ ...it, media: v })}
                                    openPicker={openPicker}
                                    mediaType={["photo", "photo_360", "video", "video_360"]}
                                    testId={`hero-item-${i}-media`}
                                    helpText="Zulässig: Foto · 360°-Foto · Video · 360°-Video · Type wird automatisch erkannt und passend gerendert."
                                />
                                <TextField
                                    label="Untertitel (optional)"
                                    value={it.label || ""}
                                    onChange={(v) => upd({ ...it, label: v })}
                                    testId={`hero-item-${i}-label`}
                                    placeholder="z.B. Landhaus · Bergisches Land"
                                />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.idea":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} testId="idea-eyebrow" />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="idea-headline" />
                    <RichTextField label="Text" value={getStr("body")} onChange={(v) => set("body", v)} testId="idea-body" rows={6} helpText="Editorial-Fließtext · Absätze mit Enter, weicher Umbruch mit Shift+Enter." />
                    <MediaField
                        label="Medium"
                        mediaRef={getMedia("image")}
                        onChange={(v) => set("image", v)}
                        openPicker={openPicker}
                        mediaType={["photo", "photo_360", "video", "video_360"]}
                        staticFallbackUrl="/media/homepage/garden-detail.jpg"
                        testId="idea-image"
                    />
                </>
            );
        case "home.immersive": {
            type ImmItem = { id?: string; title?: string; enabled?: boolean; video?: unknown; poster?: unknown };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="immersive-headline" />
                    <TextareaField label="Anleitungstext" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={3} />
                    <RepeaterField<ImmItem>
                        label="360°-Videos (max 4)"
                        items={getArr<ImmItem>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ id: `i${Date.now()}`, title: "Neues Video", enabled: true, video: null, poster: null })}
                        minItems={0}
                        maxItems={4}
                        testId="immersive-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title || ""} onChange={(v) => upd({ ...it, title: v })} testId={`immersive-item-${i}-title`} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <MediaField
                                        label="360°-Video"
                                        mediaRef={it.video as MediaRef}
                                        onChange={(v) => upd({ ...it, video: v })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360", "video", "video_360"]}
                                        testId={`immersive-item-${i}-video`}
                                        helpText="Empfohlen: equirektangulares 360°-Video. Andere Medientypen sind erlaubt (kein 360°-Viewer)."
                                    />
                                    <MediaField
                                        label="Poster"
                                        mediaRef={it.poster as MediaRef}
                                        onChange={(v) => upd({ ...it, poster: v })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360", "video", "video_360"]}
                                        testId={`immersive-item-${i}-poster`}
                                    />
                                </div>
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.lenses":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RichTextField label="Text" value={getStr("body")} onChange={(v) => set("body", v)} rows={5} testId="lenses-body" />
                </>
            );
        case "home.compare": {
            type Card = { key?: string; title: string; description?: string; bullets?: string[]; highlight?: boolean; icon?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RichTextField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} testId="compare-lead" />
                    <RepeaterField<Card>
                        label="Karten"
                        items={getArr<Card>("cards")}
                        onChange={(v) => set("cards", v)}
                        newItem={() => ({ title: "Neue Karte", bullets: [""], highlight: false })}
                        minItems={1}
                        testId="compare-cards"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`compare-card-${i}-title`} />
                                <RepeaterField<string>
                                    label="Bulletpoints"
                                    items={it.bullets || []}
                                    onChange={(v) => upd({ ...it, bullets: v })}
                                    newItem={() => ""}
                                    renderItem={(b, j, upd2) => (
                                        <TextField label={`Punkt ${j + 1}`} value={b} onChange={upd2} />
                                    )}
                                />
                                <ToggleField label="Hervorgehoben (Gold-Border)" value={!!it.highlight} onChange={(v) => upd({ ...it, highlight: v })} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.use_cases": {
            type Tile = { title: string; body?: string; image?: MediaRef; skybox?: MediaRef; icon?: string; alt?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <TextareaField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} />
                    <RepeaterField<Tile>
                        label="Kacheln"
                        items={getArr<Tile>("tiles")}
                        onChange={(v) => set("tiles", v)}
                        newItem={() => ({ title: "Neue Kachel", body: "", image: null })}
                        testId="usecases-tiles"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`usecase-${i}-title`} />
                                <RichTextField label="Beschreibung" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={3} testId={`usecase-${i}-body`} />
                                <TextField label="Alt-Text (SEO)" value={it.alt || ""} onChange={(v) => upd({ ...it, alt: v })} />
                                <MediaField label="Medium" mediaRef={it.image} onChange={(v) => upd({ ...it, image: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360", "video", "video_360"]} staticFallbackUrl={USE_CASES_FALLBACK[i]?.image} testId={`usecase-${i}-image`} />
                                <MediaField label="Skybox (360° optional)" mediaRef={it.skybox} onChange={(v) => upd({ ...it, skybox: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360", "video", "video_360"]} helpText="Nur 360°-Foto wird als Skybox gerendert – andere Medien ignoriert." />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.gallery": {
            type Tile = { image?: MediaRef; enabled?: boolean };
            const legacyTiles = getArr<Tile>("tiles");
            const entries = legacyTiles.map((t) => ({
                ref: (t.image ?? null) as MediaRef,
                enabled: t.enabled !== false,
            }));
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <TextareaField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} />
                    <MediaListField
                        label="Galerie-Medien"
                        items={entries}
                        // Nur `image` + `enabled` schreiben; sonst überschreibt der
                        // Seed-Rest von "Landhaus · …" die User-Uploads im Frontend.
                        onChange={(next) => set("tiles", next.map((e) => ({ image: e.ref, enabled: e.enabled !== false })))}
                        openPicker={openPicker}
                        mediaType={["photo", "photo_360", "video", "video_360"]}
                        withVisibility
                        helpText="Zulässig: Foto · 360°-Foto · Video · 360°-Video. Type wird automatisch erkannt und im Frontend passend gerendert. Per Sichtbarkeits-Toggle können Kacheln temporär verborgen werden, ohne sie zu löschen."
                        testId="gallery-list"
                    />
                </>
            );
        }
        case "home.before_after": {
            type Pair = { id?: string; title?: string; vorher?: MediaRef; nachher?: MediaRef };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <TextareaField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} />
                    <RepeaterField<Pair>
                        label="Vorher/Nachher-Paare (überschreiben die Projekte-Ansicht)"
                        items={getArr<Pair>("pairs")}
                        onChange={(v) => set("pairs", v)}
                        newItem={() => ({ id: `pair-${Date.now()}`, title: "Neues Paar" })}
                        testId="ba-pairs"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Label" value={it.title || ""} onChange={(v) => upd({ ...it, title: v })} testId={`ba-pair-${i}-title`} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <MediaField label="Vorher" mediaRef={it.vorher} onChange={(v) => upd({ ...it, vorher: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360", "video", "video_360"]} testId={`ba-pair-${i}-vorher`} />
                                    <MediaField label="Nachher" mediaRef={it.nachher} onChange={(v) => upd({ ...it, nachher: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360", "video", "video_360"]} testId={`ba-pair-${i}-nachher`} />
                                </div>
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.transformations360": {
            type Tr360Item = {
                id?: string;
                title?: string;
                description?: string;
                thumbnail?: MediaRef;
                before?: MediaRef;
                after?: MediaRef;
            };
            const newId = () => {
                try {
                    return crypto.randomUUID();
                } catch {
                    return `tr360-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                }
            };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} testId="tr360-eyebrow" />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="tr360-headline" />
                    <TextField label="Subheadline" value={getStr("subheadline")} onChange={(v) => set("subheadline", v)} testId="tr360-subheadline" />
                    <TextareaField label="Intro-Text" value={getStr("intro")} onChange={(v) => set("intro", v)} rows={3} testId="tr360-intro" />
                    <div className="text-xs text-muted-foreground mt-2 mb-1 leading-relaxed">
                        Empfohlen: 360°-Foto für Vorher/Nachher (aktiviert den 360°-Slider). Andere Medientypen sind erlaubt, werden aber als Standard-Bild/-Video angezeigt.
                    </div>
                    <RepeaterField<Tr360Item>
                        label="360°-Transformationen"
                        items={getArr<Tr360Item>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ id: newId(), title: "Neue Transformation", description: "" })}
                        testId="tr360-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title || ""} onChange={(v) => upd({ ...it, title: v })} testId={`tr360-item-${i}-title`} />
                                <TextareaField label="Beschreibung" value={it.description || ""} onChange={(v) => upd({ ...it, description: v })} rows={2} testId={`tr360-item-${i}-description`} />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <MediaField
                                        label="Vorher"
                                        mediaRef={it.before}
                                        onChange={(v) => upd({ ...it, before: v || undefined })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360", "video", "video_360"]}
                                        testId={`tr360-item-${i}-before`}
                                    />
                                    <MediaField
                                        label="Nachher"
                                        mediaRef={it.after}
                                        onChange={(v) => upd({ ...it, after: v || undefined })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360", "video", "video_360"]}
                                        testId={`tr360-item-${i}-after`}
                                    />
                                    <MediaField
                                        label="Thumbnail"
                                        mediaRef={it.thumbnail}
                                        onChange={(v) => upd({ ...it, thumbnail: v || undefined })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360", "video", "video_360"]}
                                        testId={`tr360-item-${i}-thumbnail`}
                                    />
                                </div>
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.process": {
            type Step = { step: string; title: string; body?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RichTextField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} testId="process-lead" helpText="Editorial-Intro über den Schritten (Fließtext, ohne die Schritt-Beschreibungen)." />
                    <RepeaterField<Step>
                        label="Schritte"
                        items={getArr<Step>("steps")}
                        onChange={(v) => set("steps", v)}
                        newItem={() => ({ step: "05", title: "Neuer Schritt", body: "" })}
                        maxItems={5}
                        testId="process-steps"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <div className="grid grid-cols-4 gap-3">
                                    <TextField label="Nummer" value={it.step} onChange={(v) => upd({ ...it, step: v })} />
                                    <div className="col-span-3">
                                        <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`process-${i}-title`} />
                                    </div>
                                </div>
                                <TextareaField label="Beschreibung" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={2} />
                            </div>
                        )}
                    />
                    <TextField label="Optional-Notiz (unter den Schritten)" value={getStr("optional_note")} onChange={(v) => set("optional_note", v)} />
                </>
            );
        }
        case "home.packages": {
            type Pkg = { key?: string; name: string; recommended?: boolean; bullets: string[]; price?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <TextareaField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} />
                    <RepeaterField<Pkg>
                        label="Pakete"
                        items={getArr<Pkg>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ key: `pkg-${Date.now()}`, name: "Neues Paket", bullets: [""], recommended: false, price: "Preis auf Anfrage" })}
                        testId="packages-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} testId={`pkg-${i}-name`} />
                                <TextField label="Preis" value={it.price || ""} onChange={(v) => upd({ ...it, price: v })} />
                                <RepeaterField<string>
                                    label="Features"
                                    items={it.bullets || []}
                                    onChange={(v) => upd({ ...it, bullets: v })}
                                    newItem={() => ""}
                                    renderItem={(b, j, upd2) => (
                                        <TextField label={`Feature ${j + 1}`} value={b} onChange={upd2} />
                                    )}
                                />
                                <ToggleField label="Empfohlen (hervorgehoben)" value={!!it.recommended} onChange={(v) => upd({ ...it, recommended: v })} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.about":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RichTextField label="Text" value={getStr("body")} onChange={(v) => set("body", v)} rows={6} testId="about-body" helpText="Editorial-Fließtext · Marker über die Toolbar." />
                    <MediaField
                        label="Medium"
                        mediaRef={getMedia("image")}
                        onChange={(v) => set("image", v)}
                        openPicker={openPicker}
                        mediaType={["photo", "photo_360", "video", "video_360"]}
                        staticFallbackUrl="/media/homepage/about-editorial.jpg"
                        testId="about-image"
                    />
                </>
            );
        case "home.faq": {
            type QA = { q: string; a: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RepeaterField<QA>
                        label="Fragen und Antworten"
                        items={getArr<QA>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ q: "Neue Frage?", a: "" })}
                        testId="faq-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Frage" value={it.q} onChange={(v) => upd({ ...it, q: v })} testId={`faq-${i}-q`} />
                                <TextareaField label="Antwort" value={it.a} onChange={(v) => upd({ ...it, a: v })} rows={3} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "home.contact":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <TextareaField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="Telefon" value={getStr("phone")} onChange={(v) => set("phone", v)} testId="contact-phone" />
                        <TextField label="E-Mail" value={getStr("email")} onChange={(v) => set("email", v)} testId="contact-email" />
                    </div>
                    <TextareaField label="Anschrift" value={getStr("address")} onChange={(v) => set("address", v)} rows={2} testId="contact-address" />
                    <TextField label="WhatsApp-URL (optional)" value={getStr("whatsapp_url")} onChange={(v) => set("whatsapp_url", v)} />
                </>
            );
        default:
            return <div className="text-sm text-hf-muted">Unbekannte Section.</div>;
    }
}
